// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { ProductContextModel } from "@insite/client-framework/Components/ProductContext";
import { getProductState, getProductStateByPath } from "@insite/client-framework/Store/Data/Products/ProductsSelectors";
import { CartLineModel, DocumentModel, OrderLineModel, ProductDto, ProductModel } from "@insite/client-framework/Types/ApiModels";
import { parseBoolean } from "@synd/XDL/Utilities";
import cloneDeep from "lodash/cloneDeep";

type VideoAndDocumentSelector = { videos: DocumentModel[]; others: DocumentModel[] };
export const getProductVideosAndDocumentsState = (productDocuments?: DocumentModel[]) => {
    const documents = productDocuments || [];
    const youtubeVimeoRegex =
        /^.*((youtu.be\/|vimeo.com\/)|(v\/)|(\/u\/\w\/)|(embed\/)|(video\/)|(watch\?))\??v?=?([^#\&\?]*).*/i;
    const documentCategories = documents.reduce(
        (acc: VideoAndDocumentSelector, document) => {
            if (
                document.documentType === "video" ||
                document.filePath.match(youtubeVimeoRegex) ||
                document.filePath.includes(".mp4")
            ) {
                acc.videos.push(document);
            } else {
                acc.others.push(document);
            }
            return acc;
        },
        { videos: [], others: [] },
    );

    return documentCategories;
};

// it currently doesn't appear that we need to memoize this. We may need to later after we resolve some issues with DataState not caching results as well as it could.
export function getComputedVariantProduct(
    parentProductState: ReturnType<typeof getProductStateByPath>,
    variantProductState: ReturnType<typeof getProductState>,
) {
    if (!variantProductState.value) {
        return parentProductState;
    }

    if (!parentProductState.value) {
        return parentProductState;
    }

    if (parentProductState.value.id === variantProductState.value.id) {
        return parentProductState;
    }

    const { value: variantProduct } = variantProductState;

    const computedProduct = cloneDeep(parentProductState.value);
    Object.assign(computedProduct, {
        productNumber: variantProduct.productNumber,
        customerProductNumber: variantProduct.customerProductNumber,
        smallImagePath: variantProduct.smallImagePath,
        mediumImagePath: variantProduct.mediumImagePath,
        largeImagePath: variantProduct.largeImagePath,
        imageAltText: variantProduct.imageAltText,
        id: variantProduct.id,
        quoteRequired: variantProduct.quoteRequired,
        productTitle: variantProduct.productTitle,
        images: variantProduct.images,
        trackInventory: variantProduct.trackInventory,
        minimumOrderQty: variantProduct.minimumOrderQty,
        canonicalUrl: variantProduct.canonicalUrl,
        unitOfMeasures: variantProduct.unitOfMeasures,
        isVariantParent: false,
        canAddToWishlist: variantProduct.canAddToWishlist,
        cantBuy: variantProduct.cantBuy,
        allowZeroPricing: variantProduct.allowZeroPricing,
        manufacturerItem: variantProduct.manufacturerItem,
        detail: variantProduct.detail,
        content:
            variantProduct.content && variantProduct.content.htmlContent !== ""
                ? variantProduct.content
                : parentProductState.value.content,
        documents:
            variantProduct.documents && variantProduct.documents.length > 0
                ? variantProduct.documents.concat(parentProductState.value.documents || [])
                : parentProductState.value.documents,
        specifications:
            variantProduct.specifications && variantProduct.specifications.length > 0
                ? variantProduct.specifications
                : parentProductState.value.specifications,
        attributeTypes:
            variantProduct.attributeTypes && variantProduct.attributeTypes.length > 0
                ? variantProduct.attributeTypes
                : parentProductState.value.attributeTypes,
        badges:
            variantProduct.badges && variantProduct.badges.length > 0
                ? variantProduct.badges
                : parentProductState.value.badges,

        // XNG-Change
        brand: variantProduct.brand || computedProduct.brand,
        packDescription: variantProduct.packDescription,
        // XNG-Change: merge variant properties (e.g. casesSkid) over parent properties
        properties: {
            ...parentProductState.value.properties,
            ...variantProduct.properties,
        },
    });

    return {
        isLoading: parentProductState.isLoading,
        id: variantProductState.id,
        value: computedProduct,
    };
}

export const isNotActiveProduct = (product: ProductModel | CartLineModel | OrderLineModel) => {
    return (
        product.isDiscontinued ||
        ("isActive" in product && !product.isActive) ||
        ("isActiveProduct" in product && !product.isActiveProduct)
    );
};

export const hasProductCustomBadges = (
    product: ProductModel | CartLineModel | OrderLineModel | ProductContextModel,
) => {
    if (!product) return {};
    const productModel = ("product" in product ? product.product : product) as ProductModel;
    const {
        wasPreviouslyPurchased,
        isPrebook,
        properties: { isPrebook: propertiesPreBook, wasPreviouslyPurchased: propertiesPreviouslyPurchased } = {},
    } = productModel as any;
    const previouslyPurchased = wasPreviouslyPurchased || parseBoolean(propertiesPreviouslyPurchased);
    const preBook = isPrebook || parseBoolean(propertiesPreBook);
    return { previouslyPurchased, preBook, hasCustomBadges: preBook || previouslyPurchased };
};

export const getProductsStatus = (products?: (ProductModel | ProductDto | null | undefined)[]) => {
    if (!products || products.length === 0) {
        return { hasPrebook: false, hasNonPrebook: false, hasMixedProducts: false };
    }

    const { hasPrebook, hasNonPrebook } = products.reduce(
        (acc, product) => {
            if (!product || (acc.hasPrebook && acc.hasNonPrebook)) {
                return acc;
            }

            const isPrebook = parseBoolean((product as any)?.properties?.isPrebook);
            return {
                hasPrebook: acc.hasPrebook || isPrebook,
                hasNonPrebook: acc.hasNonPrebook || !isPrebook,
            };
        },
        { hasPrebook: false, hasNonPrebook: false },
    );

    return { hasPrebook, hasNonPrebook, hasMixedProducts: hasPrebook && hasNonPrebook };
};
